import { FormControl, FormHelperText } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { FC } from "react";
import { Controller } from "react-hook-form";

import {
  IInput,
  isValidTimeString,
  MAIN_TIME_FORMAT,
  parseTimeAtReferenceDate,
  TimeZoneEnum,
  TTimeHHmm,
  useFormats,
  ValidationHelpers,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

export const TimeInputByControl: FC<IInput> = ({
  control,
  name,
  formLabel,
  formValidation,
  fallbackTimeHHmm,
  className,
}) => {
  const { isAMPM } = useFormats();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: ValidationHelpers.hasRequiredRule(formValidation) }}
      render={({ field, fieldState: { error } }) => {
        const { value, onChange, onBlur, ref, ...fieldRestProps } = field;

        let timeValue: Dayjs | null = null;

        if (dayjs.isDayjs(value)) {
          timeValue = value;
        } else if (isValidTimeString(value, MAIN_TIME_FORMAT)) {
          timeValue = parseTimeAtReferenceDate({
            timeHHmm: value,
            timezone: TimeZoneEnum.UTC,
          });
        } else {
          const fallbackValue = isValidTimeString(fallbackTimeHHmm)
            ? fallbackTimeHHmm
            : ZERO_TIME_HHmm;
          timeValue = parseTimeAtReferenceDate({
            timeHHmm: fallbackValue,
            timezone: TimeZoneEnum.UTC,
          });
        }

        return (
          <FormControl error={!!error} className={className}>
            <TimePicker
              name={name}
              value={timeValue}
              onChange={(val: Dayjs | null) => {
                if (!val) {
                  onChange("");
                  return;
                }

                const formattedValue = val.format(
                  MAIN_TIME_FORMAT,
                ) as TTimeHHmm;
                onChange(formattedValue);
              }}
              inputRef={ref}
              label={formLabel}
              timezone={TimeZoneEnum.UTC}
              ampm={isAMPM}
              disabled={fieldRestProps.disabled}
              slotProps={{
                textField: {
                  required: ValidationHelpers.hasRequiredRule(formValidation),
                  error: Boolean(error),
                  onBlur,
                },
              }}
            />
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};
